import type { FC } from 'react'
import { useContext, useState } from 'react'
import { Button, Header, LoadingSpinner, PhoneInput } from '@components/Generic'
import type { PhoneNumberValue } from '@components/Generic'
import type { EasySignGetContextResponse } from '@components/SignDocumentCommon/types'
import { devLog } from '@methods/devLog'
import { StoreContext } from '@store/Root.store'
import { cx } from '@utils/cx'
import { postSendOtp } from '../methods'

interface SetPhoneNumberProps {
  otpContext: EasySignGetContextResponse['smsOtp']
  resendCooldown: number
  setResendCooldown: (seconds: number) => void
  navigateToVerifyOtp: () => void
}

export const SetPhoneNumber: FC<SetPhoneNumberProps> = ({
  otpContext,
  resendCooldown,
  setResendCooldown,
  navigateToVerifyOtp,
}) => {
  const store = useContext(StoreContext)
  const { setPhoneNumberStep: transForSet, changePhoneNumberStep: transForChange } =
    store.TranslationsState.translations.easySignSmsOtpVerificationPage

  const trans =
    otpContext?.isPhoneNumberEditable && otpContext?.phoneNumber ? transForChange : transForSet

  const [isLoading, setIsLoading] = useState(false)
  const [phoneValue, setPhoneValue] = useState<PhoneNumberValue>({
    fullNumber: undefined,
    prefix: '',
    nationalNumber: '',
    isValid: false,
  })

  const canProceed = phoneValue.isValid && !isLoading && resendCooldown <= 0

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && canProceed) {
      proceed()
    }
  }

  const proceed = async () => {
    if (!phoneValue.isValid || !phoneValue.fullNumber) return

    setIsLoading(true)

    try {
      await postSendOtp(phoneValue.fullNumber)
      setResendCooldown(otpContext?.resendCooldownSeconds || 30)
      navigateToVerifyOtp()
    } catch (error) {
      devLog('Error sending OTP in SetPhoneNumber:', error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="page-container" onKeyDown={handleKeyDown}>
      <Header className="m-20-0-0">{trans.header}</Header>
      <p className="generic__paragraph-centered">{trans.description}</p>

      <div className="generic__spacer h-20" />

      <div className="layout__centered-content ">
        <PhoneInput
          disabled={isLoading}
          value={phoneValue.fullNumber}
          onChange={setPhoneValue}
          language={store.TranslationsState.language}
        />
      </div>

      <div className="generic__spacer h-30" />

      <div
        className={cx(
          'layout__content-container-bottom-fixed',
          'layout__content-container-bottom-fixed--no-border'
        )}
      >
        {otpContext?.phoneNumber && otpContext?.phoneNumber?.length > 0 && (
          <Button
            disabled={isLoading}
            onClick={isLoading ? undefined : navigateToVerifyOtp}
            className="button--secondary"
          >
            {isLoading && <LoadingSpinner inButton />}
            <span
              className={cx('generic__button-text', isLoading && 'generic__button-text--loading')}
            >
              {trans.cancel}
            </span>
          </Button>
        )}

        <Button
          disabled={!phoneValue.isValid || isLoading || resendCooldown > 0}
          onClick={isLoading ? undefined : proceed}
        >
          {isLoading && <LoadingSpinner inButton />}
          <span
            className={cx('generic__button-text', isLoading && 'generic__button-text--loading')}
          >
            {trans.proceed}
          </span>
        </Button>
      </div>
    </div>
  )
}
